import React, { memo, useCallback, useMemo, useState } from "react";
import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Pagination,
	Tooltip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { Check, Close } from "@mui/icons-material";

type OrderItem = any;

interface Props {
	dataReport: OrderItem[];
	rowsPerPage?: number;
	onViewOrder?: (order: OrderItem) => void;
}

const formatDate = (iso?: string) => {
	if (!iso) return "—";
	const d = new Date(iso);
	if (isNaN(d.getTime())) return String(iso);
	return d.toLocaleString();
};

const getTrackingDistance = (trackingDetails: any[] | undefined) => {
	if (!Array.isArray(trackingDetails) || trackingDetails.length === 0) return "—";
	const candidate = trackingDetails[1] ?? trackingDetails[trackingDetails.length - 1] ?? trackingDetails[0];
	const dist = candidate?.distance;
	return typeof dist === "number" ? dist.toFixed(2) : String(dist ?? "—");
};

const OrderRow = memo(({
	order,
	page,
	index,
	onOpen,
}: {
	order: any;
	page: number;
	index: number;
	onOpen: (o: any) => void;
}) => {
	const vendorName = order.vendorName ?? "—";
	const salesCode = order.salesPersonCode ?? order.salesPersonCode === 0 ? String(order.salesPersonCode) : "—";
	const createdAt = order.created_at ?? order.createdAt ?? order.docDate ?? null;
	const numberOrder = order.numberOrder ?? order.number ?? "—";
	const distance = getTrackingDistance(order.trackingDetails);
	const showFlag = order.showFlagDistance === true;
	const status = order.canceled ? "canceled" : order.saveOrderSap ? "saved" : "pending";
	const totalIva = typeof order.totalIva === "number" ? order.totalIva.toLocaleString("es-SV", { minimumFractionDigits: 2 }) : (order.totalIva ?? "—");
	const productsCount = Array.isArray(order.products) ? order.products.length : 0;

	return (
		<TableRow key={order.$key ?? `${page}-${index}`} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
			<TableCell>#{numberOrder}</TableCell>
			<TableCell>
				<Typography variant="body2" fontWeight={600}>{vendorName}</Typography>
				<Typography variant="caption" color="text.secondary">{order.userId ?? ""}</Typography>
			</TableCell>

			<TableCell>{salesCode}</TableCell>
			<TableCell>{formatDate(createdAt)}</TableCell>
			<TableCell sx={{ textAlign: "center" }}>{showFlag ?
				<Close sx={{ color: "#ff5c5cff" }} /> :
				<Check sx={{ color: "#5cff64ff" }} />
			}
				<Typography variant="caption" sx={{ fontWeight: 600 }} display="block" color="text.secondary">
					{distance} m
				</Typography>
			</TableCell>
			<TableCell>
				<Typography variant="body2" fontWeight={600}>{`${order.cardCode ?? "—"}`}</Typography>
				<Typography variant="caption" color="text.secondary">{`${order.cardName ?? order.cardFName ?? "—"}`}</Typography>
			</TableCell>
			<TableCell>
				{status === "saved" && <Typography sx={{ color: "#4caf50", fontWeight: 600 }}>Guardada</Typography>}
				{status === "pending" && <Typography sx={{ color: "#ff9800", fontWeight: 600 }}>Pendiente</Typography>}
				{status === "canceled" && <Typography sx={{ color: "#f55a4fff", fontWeight: 600 }}>Cancelada</Typography>}
			</TableCell>
			<TableCell>{productsCount}</TableCell>
			<TableCell sx={{ textAlign: "right" }}>$ {totalIva}</TableCell>

			<TableCell align="center">
				<Tooltip title="Ver orden">
					<IconButton size="small" onClick={() => onOpen(order)}>
						<VisibilityIcon />
					</IconButton>
				</Tooltip>
			</TableCell>
		</TableRow>
	);
}, (prevProps, nextProps) => {
	return prevProps.order === nextProps.order && prevProps.page === nextProps.page && prevProps.index === nextProps.index;
});

const SellersOrders = (props: Props) => {
	const { dataReport } = props;
	const [page, setPage] = useState<number>(1);
	const rowsPerPage = props.rowsPerPage ?? 15;
	const [openOrder, setOpenOrder] = useState<OrderItem | null>(null);

	const pagedData = useMemo(() => {
		if (!Array.isArray(dataReport)) return [];
		const start = (page - 1) * rowsPerPage;
		return dataReport.slice(start, start + rowsPerPage);
	}, [dataReport, page, rowsPerPage]);

	const pageCount = useMemo(() => {
		if (!Array.isArray(dataReport) || dataReport.length === 0) return 1;
		return Math.max(1, Math.ceil(dataReport.length / rowsPerPage));
	}, [dataReport, rowsPerPage]);

	const handleOpen = useCallback((order: OrderItem) => {
		setOpenOrder(order);
		if (props.onViewOrder) props.onViewOrder(order);
	}, [props]);

	const handleClose = useCallback(() => setOpenOrder(null), []);

	return (
		<>
			<Box sx={{ width: "100%", p: 2 }}>
				<Paper elevation={2}>
					<TableContainer sx={{ maxHeight: 640 }}>
						<Table stickyHeader aria-label="Reporte de órdenes">
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 700 }}>N°</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Vendedor</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Cod. Vendedor</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Proximidad (m)</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
									<TableCell sx={{ fontWeight: 700 }}># Prod.</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
									<TableCell sx={{ fontWeight: 700, textAlign: "center" }}>Acciones</TableCell>
								</TableRow>
							</TableHead>

							<TableBody>
								{pagedData.map((order, idx) => (
									<OrderRow key={order.$key ?? `${page}-${idx}`} order={order} page={page} index={idx} onOpen={handleOpen} />
								))}

								{(!Array.isArray(dataReport) || dataReport.length === 0) && (
									<TableRow>
										<TableCell colSpan={11} sx={{ py: 6, textAlign: "center" }}>
											<Typography color="text.secondary">No hay registros para mostrar.</Typography>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>

					<Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
						<Pagination
							count={pageCount}
							page={page}
							onChange={(_, val) => setPage(val)}
							color="primary"
							shape="rounded"
						/>
					</Box>
				</Paper>

				<Dialog open={!!openOrder} onClose={handleClose} maxWidth="lg" fullWidth>
					<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<span>Detalle de la orden</span>
						<IconButton onClick={handleClose}><CloseIcon /></IconButton>
					</DialogTitle>
					<DialogContent dividers>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>Orden (JSON):</Typography>
						<Box component="pre" sx={{ whiteSpace: "pre-wrap", fontSize: 12, background: "#f5f5f5", p: 2, borderRadius: 1, maxHeight: "60vh", overflow: "auto" }}>
							{JSON.stringify(openOrder, null, 2)}
						</Box>
					</DialogContent>
				</Dialog>
			</Box>
		</>
	);
};

export default React.memo(SellersOrders);